/**
 * 上传组件工具函数
 */
import { MEDIA_LIMIT_TYPE } from './constants';

export interface FileCheckOptions {
  file: File;
  size?: number | [number, number];
  ratio?: number | [number, number];
  duration?: number | [number, number];
}

export interface FileInfo {
  file: File;
  size: number;
  width?: number;
  height?: number;
  ratio?: number;
  duration?: number;
}

/**
 * 检查图片属性（尺寸、比例）
 */
export const checkImageAttr = ({ size, ratio, file }: FileCheckOptions): Promise<FileInfo> =>
  new Promise((resolve, reject) => {
    if (size) {
      const sizeKb = file.size / 1024;
      if (typeof size === 'number') {
        if (size < sizeKb) {
          reject(MEDIA_LIMIT_TYPE.LIMIT_SIZE);
          return;
        }
      } else if (Array.isArray(size)) {
        if (size[0] > sizeKb || size[1] < sizeKb) {
          reject(MEDIA_LIMIT_TYPE.LIMIT_SIZE_RANGE);
          return;
        }
      }
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const result = evt.target?.result as string;
      const fileInfo: FileInfo = {
        size: file.size,
        file,
      };
      const imageEle = new Image();
      imageEle.src = result;
      imageEle.onload = () => {
        const originRatio = imageEle.width / imageEle.height;
        fileInfo.ratio = originRatio;
        fileInfo.width = imageEle.width;
        fileInfo.height = imageEle.height;

        if (ratio) {
          if (typeof ratio === 'number') {
            if (ratio !== originRatio) {
              reject(MEDIA_LIMIT_TYPE.LIMIT_RATIO);
              return;
            }
          } else if (Array.isArray(ratio)) {
            if (ratio[0] > originRatio || ratio[1] < originRatio) {
              reject(MEDIA_LIMIT_TYPE.LIMIT_RATIO_RANGE);
              return;
            }
          }
        }

        resolve(fileInfo);
      };
      imageEle.onerror = () => {
        reject(MEDIA_LIMIT_TYPE.FAIL_GET_FILE_INFO);
      };
    };
    reader.onerror = () => {
      reject(MEDIA_LIMIT_TYPE.FAIL_GET_FILE_INFO);
    };
    reader.readAsDataURL(file);
  });

/**
 * 检查视频属性（尺寸、比例、时长）
 */
export const checkVideoAttr = ({ size, ratio, duration, file }: FileCheckOptions): Promise<FileInfo> =>
  new Promise((resolve, reject) => {
    if (size) {
      const sizeKb = file.size / 1024;
      if (typeof size === 'number') {
        if (size < sizeKb) {
          reject(MEDIA_LIMIT_TYPE.LIMIT_SIZE);
          return;
        }
      } else if (Array.isArray(size)) {
        if (size[0] > sizeKb || size[1] < sizeKb) {
          reject(MEDIA_LIMIT_TYPE.LIMIT_SIZE_RANGE);
          return;
        }
      }
    }

    const videoUrl = URL.createObjectURL(file);
    const videoEle = document.createElement('video');
    videoEle.preload = 'metadata';
    
    videoEle.onloadedmetadata = function (this: HTMLVideoElement) {
      window.URL.revokeObjectURL(videoUrl);

      const fileInfo: FileInfo = {
        file,
        size: file.size,
        width: this.videoWidth,
        height: this.videoHeight,
        duration: this.duration,
      };

      if (duration) {
        if (Array.isArray(duration)) {
          const [minDuration, maxDuration] = duration;
          const durationChecked = this.duration >= minDuration && this.duration <= maxDuration;
          if (!durationChecked) {
            reject(MEDIA_LIMIT_TYPE.LIMIT_DURATION_RANGE);
            return;
          }
        } else if (this.duration > duration) {
          reject(MEDIA_LIMIT_TYPE.LIMIT_DURATION);
          return;
        }
      }

      if (ratio) {
        const videoRatio = this.videoWidth / this.videoHeight;
        if (Array.isArray(ratio)) {
          const [minRatio, maxRatio] = ratio;
          const ratioChecked = videoRatio >= minRatio && videoRatio <= maxRatio;
          if (!ratioChecked) {
            reject(MEDIA_LIMIT_TYPE.LIMIT_RATIO_RANGE);
            return;
          }
        } else if (videoRatio !== ratio) {
          reject(MEDIA_LIMIT_TYPE.LIMIT_RATIO);
          return;
        }
      }

      resolve(fileInfo);
    };

    videoEle.src = videoUrl;
    videoEle.load();

    videoEle.onerror = () => {
      reject(MEDIA_LIMIT_TYPE.FAIL_GET_FILE_INFO);
    };
  });

/**
 * 获取图片信息（不校验）
 */
export const getImageInfo = ({ file }: { file: File }): Promise<FileInfo> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      const result = evt.target?.result as string;
      const fileInfo: FileInfo = {
        size: file.size,
        file,
      };
      const imageEle = new Image();
      imageEle.src = result;
      imageEle.onload = () => {
        const originRatio = imageEle.width / imageEle.height;
        fileInfo.ratio = originRatio;
        fileInfo.width = imageEle.width;
        fileInfo.height = imageEle.height;
        resolve(fileInfo);
      };
      imageEle.onerror = () => {
        reject(MEDIA_LIMIT_TYPE.FAIL_GET_FILE_INFO);
      };
    };
    reader.onerror = () => {
      reject(MEDIA_LIMIT_TYPE.FAIL_GET_FILE_INFO);
    };
    reader.readAsDataURL(file);
  });

/**
 * 获取视频信息（不校验）
 */
export const getVideoInfo = ({ file }: { file: File }): Promise<FileInfo> =>
  new Promise((resolve, reject) => {
    const videoUrl = URL.createObjectURL(file);
    const videoEle = document.createElement('video');
    videoEle.preload = 'metadata';
    
    videoEle.onloadedmetadata = function (this: HTMLVideoElement) {
      window.URL.revokeObjectURL(videoUrl);

      const fileInfo: FileInfo = {
        file,
        size: file.size,
        width: this.videoWidth,
        height: this.videoHeight,
        duration: this.duration,
      };
      resolve(fileInfo);
    };

    videoEle.src = videoUrl;
    videoEle.load();

    videoEle.onerror = () => {
      reject(MEDIA_LIMIT_TYPE.FAIL_GET_FILE_INFO);
    };
  });
